import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

const BookingPanel = ({ professional, onClose, onSubmit }) => {
  const navigate = useNavigate();
  const [selectedService, setSelectedService] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!name || !email || !selectedService) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      // Save booking to Supabase if onSubmit provided
      if (onSubmit) {
        const { error } = await onSubmit({
          service: selectedService,
          date: selectedDate,
          name,
          email,
          phone,
          notes: message,
        });
        if (error) {
          console.warn('Booking save warning:', error?.message);
          // Non-blocking - still navigate to confirmation
        }
      }
      navigate('/consultation-booking-confirmation', {
        state: {
          bookingDetails: {
            professionalName: professional?.name,
            professionalCompany: professional?.company,
            professionalPhone: professional?.phone,
            professionalEmail: professional?.email,
            professionalCategory: professional?.categoryLabel || professional?.category,
            responseTime: professional?.responseTime,
            service: selectedService,
            date: selectedDate,
            clientName: name,
            clientEmail: email,
            clientPhone: phone,
            message,
          }
        }
      });
    } catch (err) {
      setSubmitError('Failed to submit request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rounded-2xl p-6" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(0,212,255,0.15)' }}>
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-lg font-bold text-white">Book a Consultation</h3>
        {onClose && (
          <button onClick={onClose} className="p-1 rounded" style={{ color: '#6B9EC4' }}>
            <Icon name="X" size={18} />
          </button>
        )}
      </div>
      <div className="mb-4 p-3 rounded-xl" style={{ background: 'rgba(0,212,255,0.05)', border: '1px solid rgba(0,212,255,0.1)' }}>
        <div className="flex items-center gap-2 text-sm" style={{ color: '#6B9EC4' }}>
          <Icon name="Clock" size={14} color="#00D4FF" />
          Typical response: <span className="text-white font-medium">{professional?.responseTime}</span>
        </div>
      </div>

      {submitError && (
        <div className="mb-4 p-3 rounded-lg text-sm" style={{ background: 'rgba(255,80,80,0.1)', border: '1px solid rgba(255,80,80,0.2)', color: '#FF6B6B' }}>
          {submitError}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-xs font-medium mb-1.5" style={{ color: '#A0B4C8' }}>Your Name *</label>
          <input type="text" value={name} onChange={e => setName(e?.target?.value)} required placeholder="Full name"
            className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
            style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(0,212,255,0.2)', color: '#E2E8F0' }}
          />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1.5" style={{ color: '#A0B4C8' }}>Email *</label>
          <input type="email" value={email} onChange={e => setEmail(e?.target?.value)} required placeholder="your@email.com"
            className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
            style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(0,212,255,0.2)', color: '#E2E8F0' }}
          />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1.5" style={{ color: '#A0B4C8' }}>Phone</label>
          <input type="tel" value={phone} onChange={e => setPhone(e?.target?.value)} placeholder="+61 4xx xxx xxx"
            className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
            style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(0,212,255,0.2)', color: '#E2E8F0' }}
          />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1.5" style={{ color: '#A0B4C8' }}>Service Required *</label>
          <select value={selectedService} onChange={e => setSelectedService(e?.target?.value)} required
            className="w-full px-3 py-2.5 rounded-lg text-sm outline-none cursor-pointer"
            style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(0,212,255,0.2)', color: selectedService ? '#E2E8F0' : '#6B9EC4' }}
          >
            <option value="" style={{ background: '#0D1B2A' }}>Select a service...</option>
            {professional?.specializations?.map((s, i) => (
              <option key={i} value={s} style={{ background: '#0D1B2A' }}>{s}</option>
            ))}
            {(!professional?.specializations?.length) && (
              <option value="General Consultation" style={{ background: '#0D1B2A' }}>General Consultation</option>
            )}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium mb-1.5" style={{ color: '#A0B4C8' }}>Preferred Date</label>
          <input type="date" value={selectedDate} onChange={e => setSelectedDate(e?.target?.value)}
            min={new Date()?.toISOString()?.split('T')?.[0]}
            className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
            style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(0,212,255,0.2)', color: '#E2E8F0' }}
          />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1.5" style={{ color: '#A0B4C8' }}>Message</label>
          <textarea value={message} onChange={e => setMessage(e?.target?.value)} placeholder="Describe your requirements..." rows={3}
            className="w-full px-3 py-2.5 rounded-lg text-sm outline-none resize-none"
            style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(0,212,255,0.2)', color: '#E2E8F0' }}
          />
        </div>
        <button
          type="submit"
          disabled={submitting}
          className="w-full py-3 rounded-xl font-semibold text-sm transition-all duration-200 disabled:opacity-60"
          style={{ background: 'linear-gradient(135deg, #00D4FF, #0099CC)', color: '#000', boxShadow: '0 0 15px rgba(0,212,255,0.3)' }}
        >
          {submitting ? 'Submitting...' : 'Send Consultation Request'}
        </button>
      </form>
      <div className="mt-4 pt-4" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <p className="text-xs mb-3" style={{ color: '#6B9EC4' }}>Or contact directly:</p>
        <div className="flex gap-2">
          <a href={`tel:${professional?.phone}`} className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-medium" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#A0B4C8' }}>
            <Icon name="Phone" size={14} /> Call
          </a>
          <a href={`mailto:${professional?.email}`} className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-medium" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#A0B4C8' }}>
            <Icon name="Mail" size={14} /> Email
          </a>
        </div>
      </div>
    </div>
  );
};

export default BookingPanel;
